import { ComponentsTypes } from '@/constants/componentsTypes';
import { Component } from '@/schemas/sceneDataSchema';
import { RouteFn } from '@/hooks/use-flow';
import { ButtonTypes } from '@/constants/buttonTypes';
import { NavigationButton } from './formElements/buttons/NavigationButton';
import { SubmitButton } from './formElements/buttons/SubmitButton';
import { LinkTypes } from '@/constants/linkTypes';
import { HrefLink } from './formElements/typographies/HrefLink';
import { NavigationLink } from './formElements/typographies/NavigationLink';
import { ModalButton } from './formElements/buttons/ModalButton';
import { InfoCard } from './InfoCard';
import { PriceCard } from './PriceCard';
import { PartnerCommitmentPriceCard } from './PartnerCommitmentPriceCard';
import { ReloadButton } from './formElements/buttons/ReloadButton';
import { AddressFormElement } from './formElements/inputs/AddressFormElement';
import { UseFormReturn } from 'react-hook-form';
import { Image } from './Image';
import { HrefButton } from './formElements/buttons/HrefButton';
import { ExternalImage } from './ExternalImage';
import { ListItemTypes } from '@/constants/listItemTypes';
import { NavigationListItem } from './formElements/lists/NavigationListItem';
import { HrefListItem } from './formElements/lists/HrefListItem';
import { BackButton } from './formElements/buttons/BackButton';
import { ToggleListItem } from './formElements/lists/ToggleListItem';
import { AccentDivider } from './AccentDivider';
import { DividerTypes } from '@/constants/dividerTypes';
import { StandardDivider } from './StandardDivider';
import { ActionButton } from './formElements/buttons/ActionButton';
import { AnnualizedMileageElement } from './formElements/inputs/AnnualizedMileageElement';
import { TypographyBlock } from './formElements/typographies/Typography';
import { ModalLink } from './formElements/typographies/ModalLink';
import { VinImage } from './VinImage';
import { InertListItem } from './formElements/lists/InertListItem';
import { CaretLoadingIndicator } from '../CaretLoadingIndicator';
import { CookieManagementLink } from './formElements/typographies/CookieManagementLink';
import { InertIcon } from './InertIcon';
import { Alert } from './Alert';
import { Toast } from './Toast';
import { CircularProgress } from '../CircularProgress';
import { ScrollLink } from '@/components/DynamicUI/formElements/typographies/ScrollLink.tsx';
import { SteppedLoader } from '../SteppedLoader';
import { SuccessAnimation } from '../SuccessAnimation';
import { CoverageSection } from './CoverageSection';
import { Price } from './Price';
import { MediaAlphaResell } from './MediaAlphaResell';
import { CoverageSubmitButton } from './formElements/buttons/CoverageSubmitButton';

export function DisplayElementsRenderer({
  component,
  route,
  form,
}: {
  form?: UseFormReturn;
  component: Component;
  route: RouteFn;
}) {
  const renderElement = (component: Component) => {
    switch (component.type) {
      case ComponentsTypes.LOADER:
        return <CaretLoadingIndicator />;
      case ComponentsTypes.STEPPED_LOADER:
        return <SteppedLoader steps={component.steps} />;
      case ComponentsTypes.SUCCESS_ANIMATION:
        // ROUTE PROP NEEDS TO BE REMOVED WHEN POLLING IS IMPLEMENTED
        return <SuccessAnimation route={route} />;
      case ComponentsTypes.CIRCULAR_PROGRESS:
        return <CircularProgress size={component.size} />;
      case ComponentsTypes.TYPOGRAPHY:
        return <TypographyBlock component={component} />;
      case DividerTypes.STANDARD_DIVIDER:
        return <StandardDivider component={component} />;
      case DividerTypes.ACCENT_DIVIDER:
        return <AccentDivider component={component} />;
      case ComponentsTypes.IMAGE:
        return (
          <Image
            id={component.id}
            alt={component.alt}
            width={component.width}
            margin={component.margin}
            backgroundColor={component.background_color}
          />
        );
      case ComponentsTypes.VIN_IMAGE:
        return (
          <VinImage
            id={component.id}
            alt={component.alt}
            width={component.width}
          />
        );
      case ComponentsTypes.EXTERNAL_IMAGE:
        return (
          <ExternalImage
            src={component.src}
            alt={component.alt}
            width={component.width}
            margin={component.margin}
          />
        );
      case ComponentsTypes.ADDRESS:
        if (form) {
          return (
            <AddressFormElement
              route={route}
              form={form}
              component={component}
            />
          );
        }
        return null;
      case ComponentsTypes.ANNUALIZED_MILEAGE:
        if (form) {
          return (
            <AnnualizedMileageElement
              form={form}
              component={component}
            />
          );
        }
        return null;
      case ComponentsTypes.INERT_ICON:
        return (
          <InertIcon
            icon_name={component.icon_name}
            size={component.size}
            color={component.color}
          />
        );
      case ComponentsTypes.ALERT:
        return (
          <Alert
            severity={component.severity}
            title={component.title}
            titleGutterBottom={component.title_gutter_bottom}
            text={component.text}
            spans={component.spans}
            route={route}
          />
        );
      case ComponentsTypes.TOAST:
        return (
          <Toast
            id={component.id}
            severity={component.severity}
            status={component.status}
            text={component.text}
            duration={component.duration}
            anchorOrigin={component.anchor_origin}
          />
        );
      case LinkTypes.COOKIE_MANAGEMENT_LINK:
        return <CookieManagementLink component={component} />;
      case LinkTypes.HREF:
        return <HrefLink component={component} />;
      case LinkTypes.SCROLL:
        return <ScrollLink component={component} />;
      case LinkTypes.NAVIGATION:
        return (
          <NavigationLink
            component={component}
            route={route}
          />
        );
      case LinkTypes.MODAL:
        return <ModalLink component={component} />;
      case ListItemTypes.HREF:
        return <HrefListItem component={component} />;
      case ListItemTypes.NAVIGATION:
        return (
          <NavigationListItem
            component={component}
            route={route}
          />
        );
      case ListItemTypes.INERT:
        return <InertListItem component={component} />;
      case ListItemTypes.TOGGLE:
        return (
          <ToggleListItem
            form={form}
            component={component}
          />
        );
      case ButtonTypes.NAVIGATION:
        return (
          <NavigationButton
            component={component}
            route={route}
          />
        );
      case ButtonTypes.ACTION:
        return (
          <ActionButton
            component={component}
            route={route}
          />
        );
      case ButtonTypes.RELOAD:
        return <ReloadButton component={component} />;
      case ButtonTypes.BACK:
        return <BackButton component={component} />;
      case ButtonTypes.MODAL:
        return <ModalButton component={component} />;
      case ButtonTypes.HREF:
        return <HrefButton component={component} />;
      case ButtonTypes.SUBMIT:
        return <SubmitButton component={component} />;
      case ButtonTypes.COVERAGE_SUBMIT:
        return <CoverageSubmitButton component={component} />;
      case ComponentsTypes.INFO_CARD:
        return <InfoCard {...component} />;
      case ComponentsTypes.PRICE:
        return <Price component={component} />;
      case ComponentsTypes.PRICE_CARD:
        return <PriceCard component={component} />;
      case ComponentsTypes.PARTNER_COMMITMENT_PRICE_CARD:
        return <PartnerCommitmentPriceCard component={component} />;
      case ComponentsTypes.COVERAGE_SECTION:
        return (
          <CoverageSection
            symbol={component.symbol}
            apply_to={component.apply_to}
            title={component.title}
            description={component.description}
            icon={component.icon}
            modal_link={component.modal_link}
            modal={component.modal}
            route={route}
            attribute_components={component.attribute_components}
          />
        );
      case ComponentsTypes.MEDIA_ALPHA_RESELL:
        return (
          <MediaAlphaResell
            placementId={component.placement_id}
            subId={component.policyholder_driver_id}
            payload={component.prefill_data}
          />
        );
      default:
        return <>Component is not found</>;
    }
  };

  return renderElement(component);
}
